interface MiniLineChartProps {
  /** Série principal (ex.: valor da carteira). */
  series: { label: string; values: number[]; color: string }[];
  /** Rótulos do eixo X (usa primeiro/meio/último). */
  xLabels?: string[];
  height?: number;
  formatValue?: (value: number) => string;
}

/**
 * Gráfico de linha SVG leve (sem dependências) para evolução de valores.
 * Responsivo via viewBox; mostra min/max no eixo Y e três rótulos no X.
 */
export const MiniLineChart: React.FC<MiniLineChartProps> = ({
  series,
  xLabels,
  height = 220,
  formatValue = (v) => v.toLocaleString('pt-BR', { maximumFractionDigits: 0 }),
}) => {
  const allValues = series.flatMap((s) => s.values).filter((v) => Number.isFinite(v));
  if (allValues.length < 2) return null;

  const width = 800;
  const padding = { top: 12, right: 12, bottom: 24, left: 64 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const range = max - min || 1;

  const toPath = (values: number[]): string =>
    values
      .map((value, index) => {
        const x = padding.left + (index / (values.length - 1)) * innerW;
        const y = padding.top + (1 - (value - min) / range) * innerH;
        return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');

  const gridLines = [0, 0.5, 1].map((t) => padding.top + t * innerH);

  return (
    <div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        role="img"
        aria-label="Gráfico de evolução"
      >
        {gridLines.map((y, i) => (
          <line key={i} x1={padding.left} y1={y} x2={width - padding.right} y2={y} stroke="#e5e7eb" strokeWidth={1} />
        ))}
        <text x={4} y={padding.top + 4} fontSize={12} fill="#6b7280">{formatValue(max)}</text>
        <text x={4} y={height - padding.bottom} fontSize={12} fill="#6b7280">{formatValue(min)}</text>

        {series.map((s) => (
          <path key={s.label} d={toPath(s.values)} fill="none" stroke={s.color} strokeWidth={2.5} strokeLinejoin="round" />
        ))}

        {xLabels && xLabels.length >= 2 && (
          <>
            <text x={padding.left} y={height - 6} fontSize={12} fill="#6b7280">{xLabels[0]}</text>
            <text x={padding.left + innerW / 2} y={height - 6} fontSize={12} fill="#6b7280" textAnchor="middle">
              {xLabels[Math.floor(xLabels.length / 2)]}
            </text>
            <text x={width - padding.right} y={height - 6} fontSize={12} fill="#6b7280" textAnchor="end">
              {xLabels[xLabels.length - 1]}
            </text>
          </>
        )}
      </svg>
      {series.length > 1 && (
        <div className="flex gap-4 mt-2 text-xs text-gray-600">
          {series.map((s) => (
            <span key={s.label} className="inline-flex items-center gap-1.5">
              <span className="w-3 h-0.5 inline-block" style={{ backgroundColor: s.color }} />
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
